<template>
  <section :class="$style.profile">
    <header :class="[$style.profile__header, $style.section]">
      <h2 :class="$style.title">プロフィール</h2>
      <UserEssentials
        :class="[
          $style.section__content,
          $style['section__content--essentials'],
        ]"
        :user="match.user"
      />

      <ul
        :class="[$style.section__main, $style.headerContent]"
        v-if="diagnosisSummary"
      >
        <li :class="[$style.profile__diagnosis, $style.headerContent__item]">
          <component :is="DiagnosisModal">
            <DiagnosisResultModal
              v-if="karte"
              :class="$style.modal"
              :karte-id="karte.id"
              :diagnosisSummary="diagnosisSummary"
              @close="closeDiagnosisModal"
            />
          </component>
          <a :class="$style.link" @click.prevent="_openDiagnosisModal">
            診断結果
          </a>
        </li>
        <li
          :class="[
            $style.profile__concernDiagnosis,
            $style.headerContent__item,
          ]"
          v-if="karte"
        >
          <component :is="ConcernModal">
            <UserConcernAndDiagnosisModal
              :class="$style.modal"
              :karte="karte"
              @close="closeConcernModal"
            />
          </component>
          <a :class="$style.link" @click.prevent="_openConcernModal">
            問診データ
          </a>
        </li>
      </ul>
    </header>

    <section v-if="match.user.userRegistrationInflowSource">
      <div :class="$style.section__agent">
        友だち追加経路: {{ match.user.userRegistrationInflowSource }}
      </div>
    </section>

    <section v-if="match.user.financialServicesAgent">
      <div :class="$style.section__agent">
        流入元: {{ match.user.financialServicesAgent }}
      </div>
    </section>

    <section
      v-if="diagnosisSummary"
      :class="[$style.profile__section, $style.section]"
    >
      <h3 :class="$style.title">ユーザー情報</h3>
      <UserProfileInputs
        v-if="karte?.diagnosis"
        :class="$style.section__content"
        :diagnosis="karte?.diagnosis"
      />
    </section>

    <footer :class="$style.profile__footer" v-if="$slots.footer">
      <slot name="footer" />
    </footer>
  </section>
</template>

<script setup lang="ts">
import DiagnosisResultModal from './DiagnosisResultModal.vue'
import UserConcernAndDiagnosisModal from './UserConcernAndDiagnosisModal.vue'
import UserEssentials from './UserEssentials.vue'
import UserProfileInputs from './UserProfileInputs.vue'

import { useModal } from '@/hooks/useModal'
import type { DiagnosisSummary, Match } from '@/types/app'
import type { UserProfileKarteFragment } from './UserProfile.generated'

const emit = defineEmits<{
  (event: 'openDiagnosisModal'): void
  (event: 'openUserConcernAndDiagnosisModal'): void
}>()

withDefaults(
  defineProps<{
    match: Match
    diagnosisSummary?: DiagnosisSummary
    karte?: UserProfileKarteFragment
  }>(),
  {
    diagnosisSummary: undefined,
    karte: undefined,
  }
)

const { DiagnosisModal, openDiagnosisModal, closeDiagnosisModal } =
  useModal('diagnosis')
const { ConcernModal, openConcernModal, closeConcernModal } =
  useModal('concern')

const _openDiagnosisModal = () => {
  emit('openDiagnosisModal')
  openDiagnosisModal()
}

const _openConcernModal = () => {
  emit('openUserConcernAndDiagnosisModal')
  openConcernModal()
}
</script>

<style lang="scss" module>
.profile {
  $hr: 1px solid $color-border-low-emphasis;

  padding: 24px;
  background-color: $color-surface-primary;

  &__section {
    &:nth-child(n + 2) {
      margin-top: 24px;
    }
  }

  &__footer {
    &:nth-child(n + 2) {
      padding-top: 32px;
      border-top: $hr;
      margin-top: 24px;
    }
  }

  &__diagnosis {
    text-align: center;
  }

  &__concernDiagnosis {
    text-align: center;
  }
}

.title {
  font-size: 16px;
  font-weight: bold;
}

.section {
  &__content {
    &:nth-child(n + 2) {
      margin-top: 12px;
    }
  }

  &__section {
    &:nth-child(n + 2) {
      padding-top: 24px;
      margin-top: 24px;
    }
  }

  &__agent {
    padding: 10px 0;
    font-size: 10px;
    overflow-wrap: break-word;
  }

  &__main {
    $thumbnail-width: 68px;

    margin-left: $thumbnail-width;

    &:nth-child(n + 2) {
      margin-top: 6px;
    }
  }
}

.headerContent {
  display: flex;
  align-items: baseline;
  font-size: 13px;
  gap: 4px;

  &__item {
    /* divider */
    &:nth-child(n + 2) {
      &::before {
        color: $color-text-and-object-medium-emphasis;
        content: '/';
        font-size: 15px;
        font-weight: bold;
      }
    }
  }
}

.button {
  width: 100%;

  &:nth-child(n + 2) {
    margin-top: 16px;
  }
}

.link {
  color: $color-text-and-object-accent-primary;
  cursor: pointer;

  &:hover {
    color: $color-text-and-object-accent-primary;
  }
}

.modal {
  border-radius: 6px;
  box-shadow: $shadow-20;
}
</style>
